import { FolderImportantInfoWidgetStore } from './folder-important-info-widget.store';
import { Component, computed, effect, inject } from '@angular/core';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { fb, routeParams, router, sessionService, translate } from '../../../../modules/shared/shared.global-service';
import {
    folderStatusHistoryFrag,
    getIndividualPlansFrag,
    socialAcquireProcessCreateFrag,
    socialAcquireProcessFrag,
    socialAcquireProcessUpdateFrag,
} from './folder-important-info-widget.query';
import { SelectInputComponent } from '../../../kit/input/select-input/select-input.component';
import { InfiniteScrollStore } from '../../../../modules/shared/store/common/infinite-scroll.store';
import {
    FolderSocialAcquireProcessObject,
} from '@libs/types/src/services/object-type/folder/folder-social-acquire-process.object';
import { DateInputComponent } from '../../../kit/input/date-input/date-input.component';
import { TranslatePipe } from '@ngx-translate/core';
import { FolderSocialStatusEnum } from '../../../../../../../types/src/services/enum/folder-status.enum';
import {
    FolderServiceSocialRequestRejectReasonEnum,
    folderServiceSocialRequestRejectReasonOptions,
} from '@libs/types/src/services/enum/folder-social-request-reject-reason.enum';
import { CheckInputComponent } from '../../../kit/input/check-input/check-input.component';
import { Validators } from '@angular/forms';
import { contentGlobalSuccess } from '../../../../modules/content/content.error';
import {
    FolderSocialAcquireProcessUpdateInput,
} from '@libs/types/src/services/input-type/folder/folder-social-acquire-process.update-input';
import { BaseDialogService } from '../../../kit/base-dialog/base-dialog-service';
import {
    FolderPrimaryDateLabelDialogComponent,
} from './folder-primary-date-label-dialog/folder-primary-date-label-dialog.component';
import { isInvalid } from '../../../../modules/shared/helpers/validate';
import { featureOn } from '../../../../feature-flag';
import { ButtonComponent } from '../../../kit/button/button.component';
import { Divider } from '@openng/optimus-ui/divider';
import { ConfirmationService } from '@openng/optimus-ui/api';
import { AppFormGroup } from '../../../../utils/form.utils';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { Fieldset } from '@openng/optimus-ui/fieldset';
import { LinkButtonComponent } from '../../../kit/button/link-button.component';
import { WaitingListObject } from '@libs/types/src/services/object-type/waiting-list.object';
import { formatPerson } from '@libs/shared/helpers/person.utils';
import {
    WaitingListAttendantUpsertDialogComponent,
} from '../../waiting-list/waiting-list-attendant-upsert-dialog/waiting-list-attendant-upsert-dialog.component';
import {
    StripAndShowComponent,
} from '../../../../modules/shared/components/kit/strip-and-show/strip-and-show.component';
import { formatDate } from '@angular/common';
import { TagComponent } from '../../../kit/tag/tag.component';
import {
    individualPlanTargetStatusColor,
} from '@libs/types/src/services/enum/folder/individual-plan-target-status.enum';
import { ShortDatePipe } from '../../../../modules/shared/pipes/short-date.pipe';
import { EmptyStateComponent } from '../../../../modules/shared/components/empty-state/empty-state.component';

@Component({
    selector: 'app-folder-important-info-widget',
    templateUrl: './folder-important-info-widget.component.html',
    styleUrl: './folder-important-info-widget.component.scss',
    providers: [FolderImportantInfoWidgetStore],
    imports: [
        SelectInputComponent,
        DateInputComponent,
        TranslatePipe,
        CheckInputComponent,
        ButtonComponent,
        Divider,
        Tooltip,
        Fieldset,
        LinkButtonComponent,
        StripAndShowComponent,
        TagComponent,
        ShortDatePipe,
        EmptyStateComponent,
    ],
})
export class FolderImportantInfoWidgetComponent {

    public readonly store = inject(FolderImportantInfoWidgetStore);
    public readonly dialogService = inject(BaseDialogService);
    public readonly confirmationService = inject(ConfirmationService);

    protected formProcesses = new AppFormGroup({
        process: fb.control<FolderSocialAcquireProcessObject | null>(null),
    });
    protected processChanged = this.formProcesses.signalCodeChange<FolderSocialAcquireProcessObject>('process');

    protected processesScroll = computed(() => {
        return new InfiniteScrollStore<FolderSocialAcquireProcessObject>(
            this.store.api.getFolderSocialAcquireProcesses,
            FilterBuilder.filter({
                folderId: Number(routeParams.folderId()),
                sort: {
                    column: 'createdAt',
                    direction: 'DESC',
                },
            }),
            false,
            socialAcquireProcessFrag,
            (items: FolderSocialAcquireProcessObject[]) => {
                return items.map((it: FolderSocialAcquireProcessObject) => {
                    return {
                        ...it,
                        ...this.getLabelDate(it),
                    };
                });
            },
            true,
        );
    });

    protected formActProcess = new AppFormGroup({
        id: fb.control<number | null>(null),
        primaryContactDate: fb.control<Date | null>(null, Validators.required),
        primaryContactDateLabel: fb.control<string | null>(null),
        applicantInactiveFrom: fb.control<Date | null>(null),
        requestDate: fb.control<Date | null>(null),
        requestApproveDate: fb.control<Date | null>(null),
        requestCancellingDate: fb.control<Date | null>(null),
        requestCancellingReason: fb.control<string | null>(null),
        requestWithdrawnDate: fb.control<Date | null>(null),
        wishToBeInWaitingList: fb.control<boolean>(false),
        overallScore: fb.control<number | null>(null),
        readyToUseService: fb.control<boolean>(false),
    });
    protected actProcessChanged = this.formActProcess.signalCodeChange<FolderSocialAcquireProcessObject>();

    protected primaryContactDateLabel = computed(() => {
        const value = this.processChanged().value;
        if (!value) return;

        return this.getLabelDate(value);
    });

    protected contractInfo = computed(() => {
        const process = this.processChanged().value;

        if (process && process.contract) {
            return `${process.contract.contractNumber} (${formatDate(process.contract.validFrom, 'd.M.yyyy', sessionService.locale)} - ${process.contract.validTo ? ` ${formatDate(process.contract.validTo, 'd.M.yyyy', sessionService.locale)}` : ''})`;
        }

        return translate.instant(`${this.store.prefix}createNewContract`);
    });

    constructor() {
        this.store.onSuccess((method: string, res: any) => {
                this.processesScroll()?.refresh().then(() => {
                    this.formProcesses.patchValue({ process: this.processesScroll()?.items().find(x => x.id === res.id) });
                });
            },
            'createFolderSocialAcquireProcess',
            'updateFolderSocialAcquireProcess',
            'updateWaitingListItem',
        );

        effect(() => {
            this.getWorks();
        });

        effect(() => {
            this.getFolderStatuses();
        });

        effect(() => {
            this.getLatestIP();
        });

        effect(() => {
            const process = this.processChanged();

            if (process.value) {
                this.formActProcess.patchValue({
                    ...process.value,
                    primaryContactDate: new Date(process.value.primaryContactDate),
                    primaryContactDateLabel: process.value.primaryContactDateLabel || translate.instant('person.dashboard.importantInfo.primaryContactDate'),
                    applicantInactiveFrom: process.value.applicantInactiveFrom ? new Date(process.value.applicantInactiveFrom) : null,
                    requestDate: process.value.requestDate ? new Date(process.value.requestDate) : null,
                    requestApproveDate: process.value.requestApproveDate ? new Date(process.value.requestApproveDate) : null,
                    requestCancellingDate: process.value.requestCancellingDate ? new Date(process.value.requestCancellingDate) : null,
                    requestWithdrawnDate: process.value.requestWithdrawnDate ? new Date(process.value.requestWithdrawnDate) : null,
                    wishToBeInWaitingList: process.value.wishToBeInWaitingList !== null ? process.value.wishToBeInWaitingList : false,
                }, { isCodeUpdate: true });
            }
        });

        effect(() => {
            const isCodeUpdate = this.actProcessChanged().isCodeUpdate;
            if (isCodeUpdate || isInvalid(this.formActProcess)) return;

            this.submitChanges();
        });
    }

    protected openLabelDialog() {
        this.dialogService.open(FolderPrimaryDateLabelDialogComponent, {
            data: { acquireProcess: this.formActProcess.value },
            header: translate.instant('person.dashboard.importantInfo.primaryContactDate'),
        });
    }

    protected openWaitingListDetail(item: WaitingListObject) {
        const header = translate.instant('waitingList.attendant.titleUpdate', { folder: formatPerson(item.folder.person) });

        this.dialogService.open(WaitingListAttendantUpsertDialogComponent, {
            header,
            styleClass: 'p-dialog-width-80',
            data: { item },
        });
    }

    protected openContractDialog(id: number | null | undefined = null) {
        router.navigate([`/content/social/folders/${routeParams.folderId()}/contracts/${id || 'create'}`]);
    }

    protected openIPDetail() {
        router.navigate([`/content/social/folders/${routeParams.folderId()}/individual-plan`]);
    }

    protected addRequest(event: any) {
        const currentDate = new Date;
        this.confirmationService.confirm({
            target: event.target as EventTarget,
            message: translate.instant('person.dashboard.importantInfo.messages.reallyCreateNewRequest'),
            acceptLabel: translate.instant('yes'),
            rejectLabel: translate.instant('no'),
            rejectButtonStyleClass: 'p-button-secondary',
            key: 'confirmPopup',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.store.api.createFolderSocialAcquireProcess({
                    folderId: Number(routeParams.folderId()),
                    primaryContactDate: currentDate.toISOString(),
                    primaryContactDateLabel: translate.instant('person.dashboard.importantInfo.requestCreationDate'),
                    requestDate: currentDate.toISOString(),
                }, socialAcquireProcessCreateFrag).then();
            },
            reject: () => {
            },
        });
    }

    private getLabelDate(item: FolderSocialAcquireProcessObject): any {
        return {
            labelDate: item.requestDate || item.primaryContactDate,
            labelDateInfo: item.primaryContactDateLabel ?
                item.primaryContactDateLabel :
                item.requestDate ?
                    'person.dashboard.importantInfo.requestDate' :
                    'person.dashboard.importantInfo.primaryContactDate',
        };
    }

    private async getWorks() {
        this.store.api.getWorks(FilterBuilder.filter({
            folderId: Number(routeParams.folderId()),
            sort: {
                column: 'dateFrom',
                direction: 'DESC',
            },
        })).then();
    }

    private async getFolderStatuses(): Promise<void> {
        const filter = new FilterBuilder({
            folderId: Number(routeParams.folderId()),
        });

        filter.validByNow('dateFrom', 'dateTo');
        filter.limit(1);

        await this.store.api.getFolderStatuses(filter.filter(), folderStatusHistoryFrag);
    }

    private async getLatestIP(): Promise<void> {
        const filter = new FilterBuilder({
            folderId: Number(routeParams.folderId()),
        });
        filter.sort('createdAt', 'DESC');
        filter.limit(1);

        await this.store.api.getIndividualPlans(filter.filter(),
            getIndividualPlansFrag.replaceAll(
                '__TARGET_FILTER__',
                new FilterBuilder()
                    .validByNow('dateFrom', 'dateTo')
                    .sort('dateFrom', 'DESC')
                    .limit(100)
                    .filterStr()
                ,
            ),
        );
    }

    private async submitChanges() {
        await this.store.api.updateFolderSocialAcquireProcess(this.prepareSubmitData(), socialAcquireProcessUpdateFrag);

        contentGlobalSuccess.next('common.updated');
    }

    private prepareSubmitData(): FolderSocialAcquireProcessUpdateInput {
        const data = <FolderSocialAcquireProcessUpdateInput>{
            id: this.formActProcess.value.id,
            primaryContactDate: this.formActProcess.value.primaryContactDate!.toISOString(),
            primaryContactDateLabel: this.formActProcess.value.primaryContactDateLabel,
        };

        if (this.store.actFolderStatus() === FolderSocialStatusEnum.APPLICANT && !this.formActProcess.value.requestDate) {
            data.applicantInactiveFrom = this.formActProcess.value.applicantInactiveFrom?.toISOString() || null;
        } else {
            data.applicantInactiveFrom = null;
        }

        if (!this.formActProcess.value.applicantInactiveFrom) {
            data.requestDate = this.formActProcess.value.requestDate?.toISOString() || null;
        } else {
            data.requestDate = null;
        }

        if (this.formActProcess.value.requestDate) {
            data.requestApproveDate = this.formActProcess.value.requestApproveDate?.toISOString() || null;
            data.requestCancellingDate = this.formActProcess.value.requestCancellingDate?.toISOString() || null;
            data.requestWithdrawnDate = this.formActProcess.value.requestWithdrawnDate?.toISOString() || null;
        } else {
            data.requestApproveDate = null;
            data.requestCancellingDate = null;
            data.requestWithdrawnDate = null;
        }

        if (this.formActProcess.value.requestCancellingDate) {
            data.requestCancellingReason = this.formActProcess.value.requestCancellingReason;
        } else {
            data.requestCancellingReason = null;
        }

        if (this.store.currentService()?.social?.useWaitingList) {
            data.wishToBeInWaitingList = this.formActProcess.value.wishToBeInWaitingList;
        } else {
            data.wishToBeInWaitingList = null;
        }

        return data;
    }

    protected readonly FolderServiceTypeSocialStatusEnum = FolderSocialStatusEnum;
    protected readonly folderServiceSocialRequestRejectReasonOptions = folderServiceSocialRequestRejectReasonOptions;
    protected readonly FolderServiceSocialRequestRejectReasonEnum = FolderServiceSocialRequestRejectReasonEnum;
    protected readonly featureOn = featureOn;
    protected readonly individualPlanTargetStatusColor = individualPlanTargetStatusColor;
}
